"use client";

/**
 * LocationChip — header chip + dropdown that lets the buyer switch their
 * active ship-to location.
 *
 * Visibility matrix (P13-T12 design):
 *   - Anonymous / pending / rejected buyers: hidden (isApproved = false).
 *   - Approved buyer, 0 locations: hidden.
 *   - Approved buyer, 1 location: visible + disabled (no dropdown).
 *   - Approved buyer, 2+ locations: visible + enabled dropdown.
 *
 * On selection:
 *   POST /v1/storefront/cart/active-location { company_location_id }
 *   → success: refresh cart + success toast.
 *   → 422 cart.location_not_allowed: refetch locations + error toast.
 *
 * Architecture note (path (b) test approach):
 *   `deriveChipVisibility` and `handleSelect` are exported as pure/injectable
 *   functions so they can be unit-tested in the storefront's `environment: node`
 *   vitest setup without a DOM or @testing-library/react.
 *
 * AllowedLocation carries `discount_percent: number` (added in P13-T8). The chip
 * displays `name` (truncated at 22 chars) followed by `· −<n>%` when non-zero.
 * The dropdown row shows `name · postcode · −<n>%` (discount segment hidden when 0).
 */

import { useState, useEffect, useRef, useId } from "react";
import type { AllowedLocation } from "@wmart/shared-types";
import { clientApi, ApiError } from "../../lib/api-client";
import { useCart } from "@/app/[locale]/(shop)/cart/CartProvider";
import { resolveActiveLocation } from "../hooks/useActiveLocation";
import { useToast } from "@wmart/ui";

// ---------------------------------------------------------------------------
// Pure / injectable helpers (exported for unit tests)
// ---------------------------------------------------------------------------

export interface ChipVisibility {
  visible: boolean;
  disabled: boolean;
}

export function deriveChipVisibility({
  isApproved,
  locations,
}: {
  isApproved: boolean;
  locations: AllowedLocation[];
}): ChipVisibility {
  if (!isApproved || locations.length === 0) {
    return { visible: false, disabled: false };
  }
  return { visible: true, disabled: locations.length === 1 };
}

export interface HandleSelectArgs {
  locationId: string;
  locationName: string;
  /** Injectable: mirrors clientApi.post for testability */
  clientPost: (path: string, body: unknown) => Promise<unknown>;
  refresh: () => Promise<void>;
  push: (t: { tone: "success" | "danger"; title: string; body?: string }) => void;
  setLocations: (locs: AllowedLocation[]) => void;
  fetchLocations: () => Promise<AllowedLocation[]>;
}

/**
 * Executes the active-location switch mutation.
 * Injectable dependencies make this testable without a DOM.
 */
export async function handleSelect({
  locationId,
  locationName,
  clientPost,
  refresh,
  push,
  setLocations,
  fetchLocations,
}: HandleSelectArgs): Promise<void> {
  try {
    await clientPost("/v1/storefront/cart/active-location", {
      company_location_id: locationId,
    });
    // Refresh cart so re-priced lines + new companyLocationId propagate.
    await refresh();
    push({
      tone: "success",
      title: `Prices updated for ${locationName}`,
    });
  } catch (err) {
    if (
      err instanceof ApiError &&
      err.status === 422 &&
      (err.problem?.type === "cart.location_not_allowed" ||
        err.problem?.title?.toLowerCase().includes("location_not_allowed") ||
        err.problem?.title?.toLowerCase().includes("not allowed"))
    ) {
      // Location removed from customer's allowed set — refetch the list
      // so the dropdown no longer shows the stale entry.
      try {
        const fresh = await fetchLocations();
        setLocations(fresh);
      } catch {
        // silently ignore fetch failure — stale list is acceptable
      }
      push({
        tone: "danger",
        title: `${locationName} is no longer available`,
        body: "Your location list has been refreshed.",
      });
    } else {
      push({
        tone: "danger",
        title: "Could not switch location",
        body: err instanceof ApiError
          ? (err.problem?.detail ?? `HTTP ${err.status}`)
          : err instanceof Error
            ? err.message
            : "Try again",
      });
    }
  }
}

// ---------------------------------------------------------------------------
// React component
// ---------------------------------------------------------------------------

interface LocationChipProps {
  /**
   * Whether the current buyer is an approved customer. When false the chip
   * renders nothing (matching the gating used by the cart-mutate UI).
   */
  isApproved: boolean;
}

export function LocationChip({ isApproved }: LocationChipProps) {
  const { cart, refresh } = useCart();
  const toast = useToast();
  const [locations, setLocations] = useState<AllowedLocation[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const dropdownId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // ---- fetch allowed locations on mount (approved buyer only) ----
  useEffect(() => {
    if (!isApproved) return;
    let cancelled = false;
    clientApi
      .get<AllowedLocation[]>("/v1/storefront/me/allowed-locations")
      .then((data) => {
        if (!cancelled) setLocations(data);
      })
      .catch(() => {
        // 401/403 = anonymous or unapproved — locations stay empty
      });
    return () => {
      cancelled = true;
    };
  }, [isApproved]);

  // ---- close dropdown on outside click ----
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (
        buttonRef.current &&
        !buttonRef.current.closest("[data-location-chip]")?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const { visible, disabled } = deriveChipVisibility({ isApproved, locations });
  if (!visible) return null;

  const activeLoc = resolveActiveLocation(cart, locations);
  const label = activeLoc ? truncate(activeLoc.name, 22) : "Location";

  async function onSelect(loc: AllowedLocation) {
    setOpen(false);
    if (busy) return;
    setBusy(true);
    await handleSelect({
      locationId: loc.id,
      locationName: loc.name,
      clientPost: (path, body) => clientApi.post(path, body),
      refresh,
      push: toast.push,
      setLocations,
      fetchLocations: () =>
        clientApi.get<AllowedLocation[]>("/v1/storefront/me/allowed-locations"),
    });
    setBusy(false);
  }

  return (
    <div
      data-location-chip
      className="relative hidden md:block"
    >
      <button
        ref={buttonRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? dropdownId : undefined}
        aria-haspopup="listbox"
        aria-label={`Active location: ${activeLoc?.name ?? "select location"}`}
        disabled={disabled || busy}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={[
          "hidden items-center gap-1.5 rounded-sm px-2.5 py-1.5",
          "text-body-sm font-medium text-ink-700 outline-none",
          "border border-ink-200 bg-paper",
          "hover:bg-accent-soft focus-visible:shadow-focus",
          "md:inline-flex",
          disabled ? "cursor-default opacity-70" : "cursor-pointer",
          busy ? "opacity-60" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* pin icon */}
        <span aria-hidden className="text-[13px]">📍</span>
        <span className="max-w-[140px] truncate">{label}</span>
        {activeLoc && activeLoc.discount_percent > 0 && (
          <span className="shrink-0 text-accent-700 text-[11px] font-semibold">
            −{activeLoc.discount_percent}%
          </span>
        )}
        {!disabled && (
          <span aria-hidden className="ms-0.5 text-[10px] text-ink-400">
            {open ? "▲" : "▼"}
          </span>
        )}
      </button>

      {open && !disabled && (
        <ul
          id={dropdownId}
          role="listbox"
          aria-label="Select active location"
          className={[
            "absolute end-0 top-full z-50 mt-1",
            "min-w-[200px] rounded-sm border border-ink-200 bg-paper",
            "shadow-lg",
            "py-1",
          ].join(" ")}
        >
          {locations.map((loc) => {
            const isActive = loc.id === activeLoc?.id;
            return (
              <li key={loc.id} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => onSelect(loc)}
                  className={[
                    "flex w-full items-center gap-2 px-3 py-2",
                    "text-start text-body-sm",
                    "hover:bg-accent-soft focus-visible:outline-none focus-visible:bg-accent-soft",
                    isActive
                      ? "font-semibold text-ink-900"
                      : "text-ink-700",
                  ].join(" ")}
                >
                  <span
                    aria-hidden
                    className={[
                      "inline-block h-3 w-3 shrink-0 rounded-pill",
                      isActive ? "bg-accent" : "bg-transparent",
                    ].join(" ")}
                  />
                  <span className="truncate">{loc.name}</span>
                  <span className="shrink-0 text-ink-500 text-[11px]">
                    {loc.postcode}
                    {loc.discount_percent > 0 && ` · −${loc.discount_percent}%`}
                  </span>
                  {isActive && (
                    <span className="sr-only">(current)</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function truncate(str: string, max: number): string {
  if (str.length <= max) return str;
  return str.slice(0, max - 1) + "…";
}

/**
 * Returns the discount segment string for a given discount_percent value.
 * Returns `"−<n>%"` when non-zero, empty string when zero (segment is hidden).
 * Exported for unit-testability.
 */
export function chipDiscountSegment(discountPercent: number): string {
  return discountPercent > 0 ? `−${discountPercent}%` : "";
}
